// src/App.tsx
import { Suspense } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ConfigProvider, Spin } from "antd";
import { ThemeProvider as SCThemeProvider } from "styled-components";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import { GlobalStyle } from "./styles/GlobalStyle";
import { theme } from "./theme";

import AdminLayout from "src/layout/Layout";
import CustomSnackbar from "src/components/common/CustomSnackbar";

import LoginPage from "src/pages/LoginPage";
import LoginCallback from "src/pages/LoginCallback";
import NotFoundPage from "src/pages/NotFoundPage";
import ErrorPage from "src/pages/ErrorPage";
import PartnerRegisterPage from "./pages/PartnerRegisterPage";

import { isAuthed } from "src/utils/tokenCookie";
import { useInitAuthCookie } from "src/hooks/useInitAuthCookie";
import { useAuthStore, isRouteAllowed } from "src/store/Auth/auth.store";
import { routeComponentMap } from "src/config/routeComponents";

const queryClient = new QueryClient();

// Lazy loading 시 로딩 표시
function PageLoader() {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        minHeight: 200,
      }}
    >
      <Spin />
    </div>
  );
}

function ProtectedLayout() {
  const location = useLocation();
  const menuTree = useAuthStore((s) => s.menuTree);

  if (!isAuthed()) return <Navigate to="/login" replace />;

  if (menuTree.length > 0 && !isRouteAllowed(menuTree, location.pathname)) {
    return <Navigate to="/error/403" replace />;
  }

  return (
    <AdminLayout>
      <Suspense fallback={<PageLoader />}>
        <Outlet />
      </Suspense>
    </AdminLayout>
  );
}

function LoginEntryRoute() {
  const [params] = useSearchParams();
  const code = params.get("code");

  // if (isAuthed()) return <Navigate to="/" replace />;
  if (code) return <LoginCallback />;
  return <LoginPage />;
}

// routeComponentMap에서 동적으로 라우트 생성 (대시보드 포함)
function renderDynamicRoutes() {
  return Object.entries(routeComponentMap).map(([path, Component]) => {
    return <Route key={path} path={path} element={<Component />} />;
  });
}

export default function App() {
  const initialized = useInitAuthCookie();
  if (!initialized) return null;
  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider theme={theme}>
        <SCThemeProvider theme={theme}>
          <GlobalStyle />
          <BrowserRouter>
            <Routes>
              {/* 공개 라우트 */}
              <Route path="/login" element={<LoginEntryRoute />} />
              <Route path="/v1/admin/partner/register" element={<PartnerRegisterPage />} />

              {/* 보호된 라우트 - routeComponentMap에서 동적 생성 (대시보드 포함) */}
              <Route element={<ProtectedLayout />}>
                {renderDynamicRoutes()}

                {/* 에러 페이지 */}
                <Route path="/error/404" element={<ErrorPage status={404} />} />
                <Route path="/error/403" element={<ErrorPage status={403} />} />
                <Route path="/error/500" element={<ErrorPage status={500} />} />
              </Route>

              {/* 404 */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>

            <CustomSnackbar />
          </BrowserRouter>

          <ReactQueryDevtools initialIsOpen={false} />
        </SCThemeProvider>
      </ConfigProvider>
    </QueryClientProvider>
  );
}
